import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from 'react'
import styles from './styles'
import { withStyles } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'
import {
  aICreatorGetDocumentHeadersAction,
  aICreatorSuggestPageTitleAction,
} from 'src/reducers/ai-creator/action'
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
} from '@material-ui/core'
import { CoreTextField } from '../core'
import TextFieldPopup from 'src/components/textfield-popup'
import SuggestionSkeleton from 'src/components/suggestion-skeleton'
import { Icon } from '@iconify/react'
import { CoreButton } from '../core'
import { isMobile } from 'react-device-detect'
const flattenHeaders = (
  headers,
  level = 0,
  parentTitle = '',
  attachmentData = null,
) => {
  let flattened = []
  headers.forEach(header => {
    flattened.push({
      ...header,
      level,
      attachment_id: attachmentData?.id,
      url: attachmentData?.url,
      fileName: attachmentData?.name,
    })
    if (header.children && header.children.length > 0) {
      flattened = flattened.concat(
        flattenHeaders(header.children, level + 1, parentTitle, attachmentData),
      )
    }
  })
  return flattened
}
const AddSubheaderModal = forwardRef(
  (
    { outline, outlineId, classes, moduleIndex, sectionIndex, onTitleSelect },
    ref,
  ) => {
    const dispatch = useDispatch()
    const [title, setTitle] = useState('')
    const [documentHeaders, setDocumentHeaders] = useState([])
    const [flattenedHeaders, setFlattenedHeaders] = useState([])
    const [selectedTitle, setSelectedTitle] = useState(null)
    const [pageType, setPageType] = useState('info')
    const [showError, setShowError] = useState(false)
    const [showSelectionError, setShowSelectionError] = useState(false)
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [pageTitleSuggestions, setPageTitleSuggestions] = useState([])
    const hasRequestedSuggestions = useRef(false)
    const titleInputRef = useRef(null)
    const attachments = useSelector(state => state.aICreator.attachments)
    const courseTopic = useSelector(state => state.aICreator.topic)

    useImperativeHandle(ref, () => ({
      setShowError: value => {
        setShowError(value)
      },
      resetState: () => {
        setTitle('')
        setSelectedTitle(null)
        setShowError(false)
        setShowSelectionError(false)
        setPageType('info')
        setShowSuggestions(false)
        setPageTitleSuggestions([])
        hasRequestedSuggestions.current = false
      },
      validateSelection: () => {
        // Check if we have either a selected document header or a custom title
        if (!selectedTitle && (!title || title.trim().length < 2)) {
          setShowSelectionError(true)
          return false
        }
        return true
      },
      getState: () => ({
        title,
        pageType,
        selectedTitle,
      }),
    }))

    const handleTitleFocus = useCallback(() => {
      // Show suggestions when focusing on empty title or short title
      if (!title || title.trim() === '' || title.trim().length < 3) {
        // Prevent multiple API calls
        if (hasRequestedSuggestions.current) {
          return
        }

        setShowSuggestions(true)
        setIsLoading(true)
        hasRequestedSuggestions.current = true

        // Extract course data from outline for better suggestions
        const currentGoals = []
        const currentPageTitles = []
        const currentSectionTitles = []

        if (outline?.sections) {
          outline.sections.forEach((section, idx) => {
            // Collect section titles for context
            if (section.section_title && section.section_title.trim() !== '') {
              currentSectionTitles.push(section.section_title)
            }

            if (section.modules) {
              section.modules.forEach(module => {
                // Collect goals
                if (module.outline && module.outline !== null) {
                  currentGoals.push(
                    ...module.outline.filter(
                      goal => goal && goal.trim() !== '',
                    ),
                  )
                }
                // Collect existing page titles
                if (module.module_title && module.module_title.trim() !== '') {
                  currentPageTitles.push(module.module_title)
                }
              })
            }
          })
        }

        // Get current section context for better suggestions
        const currentSection = outline?.sections?.[sectionIndex]
        const sectionContext = currentSection
          ? {
              sectionTitle: currentSection.section_title || '',
              sectionGoals: currentSection.outline || [],
              sectionModules: currentSection.modules || [],
            }
          : null

        // Call the API to get suggestions
        const requestData = {
          outlineId: outlineId || '',
          type: pageType,
          sectionTitle: outline?.sections?.[sectionIndex]?.section_title || '',
        }
        dispatch(
          aICreatorSuggestPageTitleAction(requestData, response => {
            let suggestions = []
            if (response && response.suggestedPageTitles) {
              suggestions = response.suggestedPageTitles
            }
            setPageTitleSuggestions(suggestions)
            setIsLoading(false)
          }),
        )
      }
    }, [title, outline, attachments, dispatch, courseTopic, sectionIndex])

    const handleSuggestionClick = suggestion => {
      setTitle(suggestion)
      setShowSuggestions(false)
      hasRequestedSuggestions.current = false

      // Update parent with the selected suggestion
      if (onTitleSelect) {
        onTitleSelect({
          text: suggestion,
          module_title: suggestion,
          content_type: pageType,
          element_id: `custom_${Date.now()}`,
          attachment_id: null,
          url: null,
          fileName: null,
          page_start: null,
          page_end: null,
        })
      }
    }

    const handleRegenerate = () => {
      setIsLoading(true)

      // Extract course data from outline for better suggestions
      const currentGoals = []
      const currentPageTitles = []
      const currentSectionTitles = []

      if (outline?.sections) {
        outline.sections.forEach((section, idx) => {
          // Collect section titles for context
          if (section.section_title && section.section_title.trim() !== '') {
            currentSectionTitles.push(section.section_title)
          }

          if (section.modules) {
            section.modules.forEach(module => {
              // Collect goals
              if (module.outline && module.outline !== null) {
                currentGoals.push(
                  ...module.outline.filter(goal => goal && goal.trim() !== ''),
                )
              }
              // Collect existing page titles
              if (module.module_title && module.module_title.trim() !== '') {
                currentPageTitles.push(module.module_title)
              }
            })
          }
        })
      }

      // Get current section context for better suggestions
      const currentSection = outline?.sections?.[sectionIndex]
      const sectionContext = currentSection
        ? {
            sectionTitle: currentSection.section_title || '',
            sectionGoals: currentSection.outline || [],
            sectionModules: currentSection.modules || [],
          }
        : null

      const requestData = {
        outlineId: outlineId || '',
        type: pageType,
        sectionTitle: outline?.sections?.[sectionIndex]?.section_title || '',
      }

      dispatch(
        aICreatorSuggestPageTitleAction(requestData, response => {
          let suggestions = []
          if (response && response.suggestedPageTitles) {
            suggestions = response.suggestedPageTitles
          }
          setPageTitleSuggestions(suggestions)
          setIsLoading(false)
        }),
      )
    }

    const handlePopupClose = useCallback(() => {
      setShowSuggestions(false)
      hasRequestedSuggestions.current = false
    }, [])

    useEffect(() => {
      dispatch(
        aICreatorGetDocumentHeadersAction(outlineId, response => {
          if (response) {
            let allHeaders = []
            response.forEach((doc, index) => {
              if (doc?.attachment?.headers) {
                const flattened = flattenHeaders(
                  doc.attachment.headers,
                  0,
                  '',
                  doc.attachment,
                )
                allHeaders.push({
                  fileName: doc.attachment.name,
                  headers: flattened,
                })
              }
            })
            setFlattenedHeaders(allHeaders)
          }
        }),
      )
    }, [])

    useEffect(() => {
      // Focus the title field when modal opens
      const timer = setTimeout(() => {
        const titleInput = document.querySelector('input[type="text"]')
        if (titleInput) {
          titleInput.focus()
        }
      }, 100)
      return () => clearTimeout(timer)
    }, [])

    useEffect(() => {
      // Reset error state when title changes to valid state
      if (title.trim()) {
        if (title.trim().length >= 2) {
          setShowError(false)
        }
      }
    }, [title])

    // Update loading state when suggestions are received
    useEffect(() => {
      if (pageTitleSuggestions.length > 0) {
        setIsLoading(false)
      }
    }, [pageTitleSuggestions])

    const handleTitleChange = e => {
      const newTitle = e.target.value
      setTitle(newTitle)

      // Close suggestions popup when user manually types
      if (showSuggestions) {
        setShowSuggestions(false)
        hasRequestedSuggestions.current = false
      }

      // If we have a selected item, update the onTitleSelect with new title
      if (selectedTitle && onTitleSelect) {
        onTitleSelect({
          ...selectedTitle,
          text: newTitle.trim(),
          module_title: newTitle.trim(),
          content_type: pageType,
        })
      } else if (onTitleSelect && newTitle.trim().length >= 2) {
        // Even without a selected document header, call onTitleSelect with the custom title
        onTitleSelect({
          text: newTitle.trim(),
          module_title: newTitle.trim(),
          content_type: pageType,
          element_id: `custom_${Date.now()}`,
          attachment_id: null,
          url: null,
          fileName: null,
          page_start: null,
          page_end: null,
        })
      }
    }

    const handleTitleSelect = value => {
      setSelectedTitle(value)
      setShowSelectionError(false)
      if (value && onTitleSelect) {
        onTitleSelect({
          element_id: value.element_id,
          text: title.trim(),
          module_title: title.trim(),
          page_start: value.page_start,
          page_end: value.page_end,
          content_type: pageType,
          attachment_id: value.attachment_id,
          url: value.url,
          fileName: value.fileName,
          attachment: {
            id: value.attachment_id,
            name: value.fileName,
            page_start: value.page_start,
            page_end: value.page_end,
            url: value.url,
          },
        })
      }
    }

    const handlePageTypeChange = event => {
      const newPageType = event.target.value
      setPageType(newPageType)
      // Update parent when page type changes if we have a selection
      if (selectedTitle && onTitleSelect) {
        onTitleSelect({
          ...selectedTitle,
          text: title.trim(),
          module_title: title.trim(),
          content_type: newPageType,
        })
      } else if (onTitleSelect && title.trim().length >= 2) {
        // Even without a selected document header, call onTitleSelect with the custom title
        onTitleSelect({
          text: title.trim(),
          module_title: title.trim(),
          content_type: newPageType,
          element_id: `custom_${Date.now()}`,
          attachment_id: null,
          url: null,
          fileName: null,
          page_start: null,
          page_end: null,
        })
      }
    }
    return (
      <Box
        style={{
          minWidth: isMobile ? '100%' : '600px',
          maxWidth: isMobile ? '100%' : '800px',
          width: isMobile ? '100%' : '720px',
        }}
      >
        <Box className={classes.pageTypeContainer}>
          <Typography className={classes.titlePaper}>
            Select page type
          </Typography>
          <RadioGroup row value={pageType} onChange={handlePageTypeChange}>
            <FormControlLabel
              value="info"
              control={<Radio color="primary" />}
              label="Info page"
            />
            <FormControlLabel
              value="quiz"
              control={<Radio color="primary" />}
              label="Quiz page"
            />
          </RadioGroup>
        </Box>
        <CoreTextField
          value={title}
          onChange={handleTitleChange}
          variant="outlined"
          placeholder="Enter page title (at least 2 characters)"
          label="What's the title of your page? "
          fullWidth
          autoFocus
          error={showError}
          inputRef={titleInputRef}
          onFocus={handleTitleFocus}
          helperText={
            showError && 'Please enter a title with at least 2 characters'
          }
          FormHelperTextProps={{
            className: showError
              ? classes.helperTextError
              : classes.helperTextNormal,
          }}
        />

        {/* Page Title Suggestions Popup */}
        <TextFieldPopup
          anchorEl={titleInputRef.current}
          open={showSuggestions && titleInputRef.current}
          onClose={handlePopupClose}
          placement="bottom-start"
          offset={4}
          maxHeight={250}
        >
          <div className={classes.popupContent}>
            <div className={classes.popupTitleContainer}>
              <Typography
                fontSize={14}
                fontWeight={400}
                className={classes.popupTitle}
              >
                Suggested page titles:
              </Typography>
              {pageTitleSuggestions.length > 0 && (
                <CoreButton
                  size="small"
                  variant="text"
                  color="secondary"
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Icon icon="hugeicons:exchange-01" fontSize={16} />
                    )
                  }
                  onClick={handleRegenerate}
                  disabled={isLoading}
                >
                  {isLoading ? 'Generating...' : 'Regenerate'}
                </CoreButton>
              )}
            </div>

            <List dense className={classes.suggestionList}>
              {isLoading ? (
                <SuggestionSkeleton />
              ) : pageTitleSuggestions.length > 0 ? (
                pageTitleSuggestions.map((suggestion, suggestionIndex) => (
                  <ListItem
                    key={suggestionIndex}
                    button
                    className={classes.suggestionItem}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <div className={classes.suggestionItemIcon}>
                      <img
                        src="/assets/AI-Assistant-icon.svg"
                        width={isMobile ? 16 : 24}
                        height={isMobile ? 16 : 24}
                        alt="AI Assistant"
                      />
                    </div>
                    <ListItemText
                      primary={suggestion}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: 400,
                        className: classes.suggestionText,
                      }}
                    />
                  </ListItem>
                ))
              ) : (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Typography variant="body2" style={{ color: '#637381' }}>
                    {pageTitleSuggestions.length === 0
                      ? 'No suggestions available. Try regenerating.'
                      : 'Enter a course topic to get suggestions.'}
                  </Typography>
                </div>
              )}
            </List>
          </div>
        </TextFieldPopup>

        {attachments?.length > 0 && (
          <>
            <Typography className={classes.titlePaper}>
              Select content from your source
            </Typography>
            <Box>
              <Paper
                variant="outlined"
                className={`${classes.scrollableContainer} ${
                  showSelectionError ? 'error' : ''
                }`}
              >
                <List className={classes.listContainer}>
                  {flattenedHeaders.map((attachment, attachmentIndex) => (
                    <React.Fragment key={`pdf-${attachmentIndex}`}>
                      {attachmentIndex > 0 && (
                        <Divider className={classes.divider} />
                      )}
                      <ListItem className={classes.pdfListItem}>
                        <ListItemText
                          primary={attachment.fileName}
                          primaryTypographyProps={{
                            className: classes.pdfFileName,
                          }}
                        />
                      </ListItem>
                      {attachment.headers &&
                        attachment.headers.map((item, index) => (
                          <React.Fragment key={`${item.element_id}-${index}`}>
                            <ListItem
                              button
                              selected={
                                selectedTitle?.element_id === item.element_id
                              }
                              onClick={() => handleTitleSelect(item)}
                            >
                              <ListItemIcon className={classes.listItemIcon}>
                                <Radio
                                  checked={
                                    selectedTitle?.element_id ===
                                    item.element_id
                                  }
                                  size="small"
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={item.element_id}
                                primaryTypographyProps={{
                                  className: classes.contentListItem,
                                  style: {
                                    paddingLeft: item.level * 20 + 8,
                                  },
                                }}
                              />
                            </ListItem>
                          </React.Fragment>
                        ))}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
              {showSelectionError && (
                <Typography className={classes.errorText}>
                  {!title || title.trim().length < 2
                    ? 'Please enter a title with at least 2 characters'
                    : 'Please select content from the list or use your custom title'}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    )
  },
)

export default withStyles(styles)(AddSubheaderModal)
